<template>
	<!-- @vue-ignore -->
	<MsAjaxTemplate
		id="c-entrydetails"
		ref="tmpl"
		template-name="entry-details"
		:get-template-data="getTemplateData"
		:ready-dependencies="['entrydetails', 'ExoWeb.dom', 'Cognito.Forms.controller', 'admin-component-library', 'admin-form-api']"
		class="entry-details"
		:class="sizeClass"
	/>
</template>

<script lang="ts">
	import { computed, defineComponent, onMounted, ref, inject } from 'vue';
	import MsAjaxTemplate from './utils/MsAjaxTemplate';
	import { navStateKey } from 'src/utilities/injection-keys';
	import { Draggable } from '@shopify/draggable';

	const SMALL_WIDTH = 414;
	const MEDIUM_WIDTH = 800;
	const SMALL_MEDIUM_MAX_WIDTH = 525;
	const templateDataKeys = ['viewId', 'isLimitedAccess', 'canManageEntries', 'canManageForm', 'isArchived', 'disableUnreadEntryTracking', 'orderExportId'] as const;

	export default defineComponent({
		components: {
			MsAjaxTemplate
		},
		props: {
			orgcode: { type: String, required: true },
			formUrlName: { type: String, required: true },
			viewId: { type: String, required: false, default: null },
			entryNumber: { type: String, required: false, default: null },
			isLimitedAccess: { type: Boolean, required: true },
			canManageEntries: { type: Boolean, required: true },
			canManageForm: { type: Boolean, required: true },
			isArchived: { type: Boolean, default: false },
			disableUnreadEntryTracking: { type: Boolean, default: false },
			orderExportId: { type: String, default: null }
		},
		setup(props) {
			const tmpl = ref<Vue>();
			const navState = inject(navStateKey);
			const panelWidth = navState.entryDetailsWidth;

			const getSizeClasses = (width: number) => {
				const classes = [];

				// breakpoints taken from Cognito-core.js: Cognito$resize
				if (width <= SMALL_WIDTH)
					classes.push('c-sml');
				else if (width < MEDIUM_WIDTH)
					classes.push('c-med');
				else
					classes.push('c-lrg');

				if (width > SMALL_WIDTH && width <= SMALL_MEDIUM_MAX_WIDTH)
					classes.push('c-smed');

				return classes;
			};

			const sizeClass = computed(() => {
				return getSizeClasses(panelWidth.value).join(' ');
			});

			const buildTemplateData = () => {
				const data = {} as Record<string, ReturnType<typeof computed>>;

				templateDataKeys.forEach((key) => {
					data[key] = computed(() => props[key]);
				});

				return data;
			};

			onMounted(() => {
				const el = tmpl.value.$el as HTMLElement;
				const draggable = new Draggable(el, {
					draggable: '.entry-details__drag-handle',
					delay: 0,
					mirror: {
						yAxis: false
					}
				});

				draggable.on('drag:move', e => {
					const isSupportOpenAndPinned = navState.isSupportOpen.value && navState.isSupportPinned.value;
					const supportWidth = isSupportOpenAndPinned ? navState.supportWidth.value : 0;
					const width = window.innerWidth - e.sensorEvent.clientX - supportWidth;
					navState.setEntryDetailsWidth(width, false);
				});

				draggable.on('drag:stop', () => {
					navState.setEntryDetailsWidth(navState.entryDetailsWidth.value, true);
				});
			});

			const getTemplateData = () => {
				return buildTemplateData();
			};

			return { tmpl, sizeClass, getTemplateData };
		}
	});
</script>

<style lang="scss">
@import '@legacy/Views/Admin/entry-details';

:root .print\:hidden-by-entry-details {
	@media print {
		display: none !important;
	}
}
</style>